// ============= Types =============

/**
 * Strict distinction for history entries:
 * - toast: User-facing message we show as a popup; message is humanized.
 * - log:   Raw SignalR/debug data (incoming events, outbound invokes); not shown as popup.
 */
export type EntryKind = 'toast' | 'log';

/** Incoming = from server (SignalR events). Outgoing = sent by client (invokes). */
export type ToastHistoryDirection = 'incoming' | 'outgoing';

export interface ToastHistoryEntry {
  id: string;
  /** toast = user-facing; log = SignalR/debug only (no popup). */
  entryKind: EntryKind;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
  duration?: number;
  timestamp: string;
  closedAt?: string;
  source?: string; // Stack trace or source location
  /** When set, used in ToastLogPanel to show border colour: incoming vs outgoing. */
  direction?: ToastHistoryDirection;
  /** Optional workspace ID (e.g. for SignalR user events). Used for filtering in debug panel. */
  workspaceId?: string;
  /** Optional display name (e.g. user name for presence events). Used for filtering in debug panel. */
  displayName?: string;
}

/** Input for adding an entry; id and timestamp are set by the store. entryKind defaults to 'toast'. */
export type ToastHistoryEntryInput = Omit<
  ToastHistoryEntry,
  'id' | 'timestamp'
> & { entryKind?: EntryKind };

// ============= History Store (Module Pattern) =============
// Using module-level state instead of a class for simplicity
// This is a singleton by nature (module is only loaded once)

const history: ToastHistoryEntry[] = [];
const maxEntries = 500; // Keep last 500 entries
const listeners: Set<() => void> = new Set();

const TOAST_LOG_STORAGE_KEY = 'litium-toast-log-history';
const MAX_STORED_ENTRIES = 500;

const notifyListeners = () => {
  listeners.forEach((listener) => listener());
};

export const toastHistoryStore = {
  /**
   * Add a user-facing toast entry (shown as popup and in panel).
   * Use for humanized messages we intentionally show to users.
   */
  addEntry(entry: ToastHistoryEntryInput) {
    const historyEntry: ToastHistoryEntry = {
      ...entry,
      entryKind: entry.entryKind ?? 'toast',
      id: `toast-${Date.now()}-${Math.random()}`,
      timestamp: new Date().toISOString(),
    };

    history.unshift(historyEntry); // Add to beginning

    if (history.length > maxEntries) {
      history.splice(maxEntries);
    }

    notifyListeners();
  },

  /**
   * Add a log entry (SignalR events, invokes, debug). Not shown as popup.
   * Use for raw data from SignalR or outbound invokes; panel shows as "Log".
   */
  addLogEntry(entry: Omit<ToastHistoryEntryInput, 'entryKind'>) {
    this.addEntry({ ...entry, entryKind: 'log' });
  },

  markClosed(id: string) {
    const entry = history.find((e) => e.id === id);
    if (entry && !entry.closedAt) {
      entry.closedAt = new Date().toISOString();
      notifyListeners();
    }
  },

  clear() {
    history.length = 0; // Clear array
    notifyListeners();
  },

  getHistory(): ToastHistoryEntry[] {
    return [...history]; // Return a copy
  },

  subscribe(listener: () => void) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },

  /**
   * Replace history with entries from storage (e.g. for debug users on init).
   * Notifies subscribers. Use when restoring from sessionStorage.
   */
  loadFromStorage(entries: ToastHistoryEntry[]) {
    if (!Array.isArray(entries) || entries.length === 0) return;
    history.length = 0;
    const toAdd = entries.slice(0, maxEntries);
    toAdd.forEach((e) => history.push(e));
    notifyListeners();
  },

  /**
   * Serialize current history for storage. Returns JSON string or undefined.
   */
  serializeForStorage(): string | undefined {
    try {
      const copy = history.slice(0, MAX_STORED_ENTRIES).map((e) => ({
        ...e,
        id: e.id,
        timestamp: e.timestamp,
      }));
      return JSON.stringify(copy);
    } catch {
      return undefined;
    }
  },

  /** Storage key used for debug-user persistence. */
  getStorageKey(): string {
    return TOAST_LOG_STORAGE_KEY;
  },
};

// Helper function to get stack trace
export const getStackTrace = (): string | undefined => {
  try {
    throw new Error();
  } catch (e) {
    const error = e as Error;
    if (error.stack) {
      // Remove the first few lines (getStackTrace, showToast, etc.)
      const lines = error.stack.split('\n');
      // Skip first 3 lines (Error, getStackTrace, showToast)
      return lines.slice(3).join('\n');
    }
  }
  return undefined;
};
